import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { StudySettings, type StudySettings as Settings } from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { Alert, ErrorAlert, Field } from "../../components/ui/index.js";
import { GroupsEditor } from "../../components/study/GroupsEditor.js";
import { Tabs } from "../../components/ui/Layout.js";
import { useStudy } from "./StudyLayout.js";
import { useDemo } from "./AdminLayout.js";

function NumberField(props: { id: string; label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; hint?: ReactNode }) {
  return (
    <Field label={props.label} htmlFor={props.id} hint={props.hint}>
      <input
        id={props.id}
        type="number"
        className="input"
        value={Number.isFinite(props.value) ? props.value : ""}
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        onChange={(e) => props.onChange(e.target.valueAsNumber)}
      />
    </Field>
  );
}

function Check({ id, label, checked, onChange, hint }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div>
      <label htmlFor={id} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {hint && <p className="ml-6 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

type ConfigTab = "general" | "captura" | "calidad" | "grupos" | "consentimiento";
const CONFIG_TABS: ConfigTab[] = ["general", "captura", "calidad", "grupos", "consentimiento"];

/** Tarjeta de sección con título y descripción breve. */
function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-base font-bold text-ink">{title}</h2>
      {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

export function StudyConfigPage() {
  const { study, reload } = useStudy();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("seccion") as ConfigTab | null;
  const tab: ConfigTab = tabParam && CONFIG_TABS.includes(tabParam) ? tabParam : "general";
  const setTab = (t: ConfigTab) => setParams(t === "general" ? {} : { seccion: t }, { replace: true });

  const [title, setTitle] = useState(study.title);
  const [description, setDescription] = useState(study.description);
  const [protocol, setProtocol] = useState(study.protocol);
  const [consentVersion, setConsentVersion] = useState(study.consentVersion);
  const [consentText, setConsentText] = useState(study.consentText ?? "");
  const [settings, setSettings] = useState<Settings>(study.settings);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTitle(study.title);
    setDescription(study.description);
    setProtocol(study.protocol);
    setConsentVersion(study.consentVersion);
    setConsentText(study.consentText ?? "");
    setSettings(study.settings);
  }, [study]);

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    setSaved(false);
    setSettings((s) => ({ ...s, [k]: v }));
  };
  const setQ = <K extends keyof Settings["quality"]>(k: K, v: Settings["quality"][K]) => {
    setSaved(false);
    setSettings((s) => ({ ...s, quality: { ...s.quality, [k]: v } }));
  };

  const hasVideos = study.videoCount > 0;
  const locked = useDemo()?.enabled === true;
  const parsed = StudySettings.safeParse(settings);
  const hasSessions = study.sessionCounts.webgazer.total + study.sessionCounts.synthetic.total > 0;
  const dirty =
    title !== study.title ||
    description !== study.description ||
    protocol !== study.protocol ||
    consentVersion !== study.consentVersion ||
    consentText !== (study.consentText ?? "") ||
    JSON.stringify(settings) !== JSON.stringify(study.settings);

  async function save(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    setSaved(false);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "));
      return;
    }
    setBusy(true);
    try {
      await api.updateStudy(study.id, {
        title: title.trim(),
        description,
        protocol,
        consentVersion: consentVersion.trim(),
        consentText,
        settings: parsed.data,
      });
      await reload();
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div data-tour="config-tabs">
      <Tabs
        label="Secciones de la configuración"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "general", label: "General" },
          { value: "captura", label: "Captura" },
          { value: "calidad", label: "Calidad y elegibilidad" },
          { value: "grupos", label: "Grupos de edad", badge: study.groups.length },
          { value: "consentimiento", label: "Consentimiento" },
        ]}
      />
      </div>

      {tab === "grupos" ? (
        <GroupsEditor studyId={study.id} groups={study.groups} locked={hasSessions} demoLocked={locked} onSaved={reload} />
      ) : (
        <form onSubmit={save} className="space-y-4 pb-20">
          {tab === "general" && (
            <div className="grid gap-4 xl:grid-cols-2">
              <Section title="Datos generales" description="Cómo se identifica el estudio en el panel y en la pantalla de bienvenida.">
                <Field label="Nombre" htmlFor="cfg-title">
                  <input id="cfg-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
                </Field>
                <Field label="Descripción" htmlFor="cfg-desc">
                  <textarea id="cfg-desc" className="input" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>
              </Section>
              <Section title="Protocolo" description="Notas internas para el equipo investigador (no las ve el participante).">
                <Field label="Protocolo (notas para el equipo investigador)" htmlFor="cfg-protocol" hint="Condiciones de iluminación, distancia, monitor, instrucciones verbales, etc.">
                  <textarea id="cfg-protocol" className="input" rows={8} value={protocol} onChange={(e) => setProtocol(e.target.value)} />
                </Field>
              </Section>
            </div>
          )}

          {tab === "captura" && (
            <Section
              title="Presentación de estímulos"
              description="Tiempos y orden de los estímulos durante la sesión. Cada imagen se muestra el tiempo indicado; cada video se reproduce completo."
            >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <NumberField id="cfg-dur" label="Duración por imagen (ms)" value={settings.imageDurationMs} min={1000} max={60000} step={100} onChange={(v) => set("imageDurationMs", v)} hint="Predeterminado 5000 ms. Los videos duran lo que dure el video." />
                <NumberField id="cfg-fix" label="Cruz de fijación (ms)" value={settings.fixationDurationMs} min={200} max={10000} step={100} onChange={(v) => set("fixationDurationMs", v)} hint="Predeterminado 1000 ms" />
                <NumberField
                  id="cfg-reval"
                  label="Revalidar cada N estímulos"
                  value={settings.revalidateEveryNTrials}
                  min={0}
                  max={100}
                  onChange={(v) => set("revalidateEveryNTrials", v)}
                  hint={hasVideos ? "0 = sin revalidación. Con videos se recomienda 2–3." : "0 = sin revalidación intermedia"}
                />
                <NumberField id="cfg-valpts" label="Puntos de validación" value={settings.validationPoints} min={5} max={9} onChange={(v) => set("validationPoints", v)} hint="Mínimo 5; calibración fija de 9 puntos" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Check id="cfg-random" label="Aleatorizar orden por participante" checked={settings.randomizeOrder} onChange={(v) => set("randomizeOrder", v)} hint="Se guarda la semilla y el orden efectivo de cada sesión." />
                <Check id="cfg-practice" label="Ofrecer práctica (no se guarda)" checked={settings.practiceEnabled} onChange={(v) => set("practiceEnabled", v)} />
                <Check
                  id="cfg-pursuit"
                  label="Validación con punto en movimiento (~14 s)"
                  checked={settings.pursuitValidation}
                  onChange={(v) => set("pursuitValidation", v)}
                  hint={`Tras la primera calibración, la persona sigue un punto con la mirada. Mide su retraso y error reales; el análisis de video puede corregir la latencia con ese valor.${hasVideos ? " Recomendado: el estudio tiene videos." : ""}`}
                />
                <Check
                  id="cfg-audio"
                  label="Reproducir el audio de los videos"
                  checked={settings.videoAudio}
                  onChange={(v) => set("videoAudio", v)}
                  hint="Si se activa, se pide una prueba de sonido antes de empezar. Si no, los videos se reproducen silenciados. No afecta a las imágenes."
                />
              </div>
            </Section>
          )}

          {tab === "calidad" && (
            <div className="grid gap-4 xl:grid-cols-2">
              <Section title="Validación de la calibración" description="Umbrales para clasificar cada validación como aprobada, advertencia o fallida.">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Calidad mínima para iniciar" htmlFor="cfg-minq">
                    <select id="cfg-minq" className="input" value={settings.minCalibrationQuality} onChange={(e) => set("minCalibrationQuality", e.target.value as "pass" | "warn")}>
                      <option value="pass">Aprobada (pass)</option>
                      <option value="warn">Advertencia o mejor (warn)</option>
                    </select>
                  </Field>
                  <NumberField id="cfg-radius" label="Radio de validación (px)" value={settings.quality.roiRadiusPx} min={10} max={1000} onChange={(v) => setQ("roiRadiusPx", v)} />
                  <NumberField id="cfg-pass" label="Error medio máx. para «aprobada» (px)" value={settings.quality.passMaxMeanErrorPx} min={1} max={2000} onChange={(v) => setQ("passMaxMeanErrorPx", v)} />
                  <NumberField id="cfg-warn" label="Error medio máx. para «advertencia» (px)" value={settings.quality.warnMaxMeanErrorPx} min={1} max={4000} onChange={(v) => setQ("warnMaxMeanErrorPx", v)} hint="Por encima: «fallida»" />
                  <NumberField id="cfg-roi" label="% mínimo dentro del radio para «aprobada»" value={settings.quality.passMinPercentInRoi} min={0} max={100} onChange={(v) => setQ("passMinPercentInRoi", v)} />
                  <NumberField id="cfg-sps" label="Mínimo de muestras/segundo" value={settings.quality.minSamplesPerSecond} min={0} max={200} step={0.5} onChange={(v) => setQ("minSamplesPerSecond", v)} />
                </div>
                <Check
                  id="cfg-override"
                  label="Permitir continuar con baja calidad mediante autorización auditada del investigador"
                  checked={settings.allowManualOverride}
                  onChange={(v) => set("allowManualOverride", v)}
                  hint="Requiere sesión de administrador iniciada en el mismo navegador y un motivo; queda registrado."
                />
              </Section>
              <Section title="Elegibilidad y retención" description="Qué ensayos entran al análisis y cuánto tiempo se conservan las sesiones.">
                <div className="grid gap-4 md:grid-cols-2">
                  <NumberField id="cfg-ratio" label="Fracción mínima de muestras válidas por ensayo" value={settings.minimumValidSampleRatio} min={0} max={1} step={0.05} onChange={(v) => set("minimumValidSampleRatio", v)} hint="0–1. Ensayos por debajo se excluyen del análisis." />
                  <NumberField id="cfg-minsamples" label="Mínimo de muestras válidas en imagen" value={settings.minimumValidSamples} min={1} max={1000} onChange={(v) => set("minimumValidSamples", v)} />
                  <NumberField id="cfg-retention" label="Retención (días)" value={settings.deleteAfterDays} min={1} max={3650} onChange={(v) => set("deleteAfterDays", v)} hint="Sesiones más antiguas se pueden purgar en Ajustes." />
                </div>
              </Section>
            </div>
          )}

          {tab === "consentimiento" && (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              {locked && (
                <div className="xl:col-span-2">
                  <Alert tone="warning">Modo demo: el consentimiento y los datos de contacto no se pueden cambiar.</Alert>
                </div>
              )}
              <Section title="Datos del consentimiento" description="Se muestran al participante antes de activar la cámara.">
                <Field label="Versión del consentimiento" htmlFor="cfg-consent-v" hint="Cámbiala cuando modifiques el texto; cada sesión guarda la versión aceptada.">
                  <input id="cfg-consent-v" className="input" value={consentVersion} onChange={(e) => setConsentVersion(e.target.value)} maxLength={40} required disabled={locked} />
                </Field>
                <Field label="Responsable del tratamiento" htmlFor="cfg-controller">
                  <input id="cfg-controller" className="input" value={settings.dataController} onChange={(e) => set("dataController", e.target.value)} maxLength={300} disabled={locked} />
                </Field>
                <Field label="Contacto" htmlFor="cfg-contact" hint="Se muestra en el consentimiento y al final de la sesión para solicitar la eliminación de datos (p. ej. un correo del equipo).">
                  <input id="cfg-contact" className="input" value={settings.contactInfo} onChange={(e) => set("contactInfo", e.target.value)} maxLength={500} disabled={locked} />
                </Field>
              </Section>
              <Section title="Texto adicional" description="Opcional. Se añade al texto base de finalidad, datos guardados, retención y retiro.">
                <Field label="Texto adicional del consentimiento (opcional)" htmlFor="cfg-consent">
                  <textarea id="cfg-consent" className="input" rows={10} value={consentText} onChange={(e) => setConsentText(e.target.value)} disabled={locked} />
                </Field>
              </Section>
            </div>
          )}

          {!parsed.success && (
            <Alert tone="warning" title="Valores fuera de rango">
              <ul className="list-disc pl-5">
                {parsed.error.issues.map((i) => (
                  <li key={i.path.join(".") + i.message}>
                    {i.path.join(".")}: {i.message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <ErrorAlert error={error} />

          {/* Barra de guardado fija: siempre a la vista, en cualquier pestaña. */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur md:left-60" data-tour="config-save">
            <div className="mx-auto flex max-w-[1680px] items-center justify-end gap-3 px-4 py-3 md:px-7">
              <span className="mr-auto text-sm" role="status">
                {saved && !dirty ? (
                  <span className="text-emerald-700">Configuración guardada.</span>
                ) : dirty ? (
                  <span className="text-amber-700">Hay cambios sin guardar.</span>
                ) : (
                  <span className="text-slate-500">Sin cambios.</span>
                )}
              </span>
              <button type="submit" className="btn-primary" disabled={busy || !parsed.success || !dirty}>
                {busy ? "Guardando…" : "Guardar configuración"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
